import { get, camelCase, cloneDeep } from 'lodash';
import i18next from 'i18next';
import Message from '@/component/Message';
import errCode from '@/utils/request/errCode';
import {
  action, computed, observable, toJS,
} from 'mobx';
import { ConfigType as Type, ModeType } from '@/pages/home/const';
import {
  AuditStatusEnum,
  ConfigVersionTypeEnum,
  EnvList,
  InterfaceItem,
  TransPluginItem,
  TransPluginUpdateItem,
  TransPluginItemContent,
  PluginFuncLib,
  TransFuncPluginItem,
} from '@/pages/home/type/configServerType';
import {
  getEnvironmentDetail,
  getEnvironmentList,
  getInterfaceDetail,
  getInterfaceList,
  getRPCServiceName,
  getServiceInfo,
  deleteInterface,
  getNewInterfaceDetail,
  updateNewInterface,
  updateInterface,
  transferFormModel,
  transferCodeModel,
  updateInterfaceLabel,
  getPlugins,
  getPluginModel,
  getFuncPluginLib,
} from '../server/configServer';
import { getServiceList } from '@/pages/home/server/serviceManage';
import { ServiceListItem } from '@/pages/home/type/serviceManage';
import {
  buildApiConfig,
  buildApiRpcConfig,
  transformObjectToArray,
} from '../utils/config';
import { TreeNode } from '@/pages/home/type';
import { codeObjectToArray, getDefaultCode } from '../utils';
import { snakeToCamle, getQueryParams } from '@/utils/common';
import { treeForeach } from '@/pages/home/utils';
import { isComplexType, isSimpleType } from '@/pages/home/utils/config';
import { Modal } from '@byte-design/ui';
import { disabledSnakeFields } from '@/utils/const';

interface IdlInfo{
  commitId: string;
  commitTime: string;
  commitMessage: string;
}

function getUserParamInputWidth(): { [key: string]: number | undefined } {
  const localWidthJson = localStorage.getItem('user_param_input_width');
  if (localWidthJson) {
    const localWidthObj = JSON.parse(localWidthJson);
    if (localWidthObj && typeof localWidthObj === 'object') {
      return localWidthObj;
    }
    localStorage.removeItem('user_param_input_width');
  }
  return {};
}
class ConfigStore {
  // 用于支持用户自定义参数名输入框宽度
  @observable userParamInputWidth = getUserParamInputWidth();

  @observable currentServiceInfo: any = null;

  @observable serviceId = '';

  // 版本信息 (开发版本 线上版本)
  @observable versionInfo: EnvList[] = [];

  //  当前版本类型
  @observable activeEnvType: ConfigVersionTypeEnum = ConfigVersionTypeEnum.Develop;

  // 获取IDL的版本信息
  @observable idlInfo: IdlInfo = {
    commitId: '',
    commitTime: '',
    commitMessage: '',
  };

  // 获取RPC调用的方法名称
  @observable rpcFnName: Array<{label: string; value: string}> = [];

  // 当前选中的RPC字段
  @observable activeRpcName = '';

  // 获取RPC的映射字段
  @observable rpcFnNameMap: any = {};

  // 代码模式接口详情
  @observable codeModeDetail: any = {};

  // 代码模式接口详情的Copy
  @observable codeModeDetailCopy: any = {};

  // 获取接口列表
  @observable interfaceList: InterfaceItem[] = [];

  // 接口搜索的关键词
  @observable interfaceKeyword = '';

  // 接口详情
  @observable interfaceDetail: any = null;

  // 接口详情的副本 用来欢迎接口数据修改
  @observable interfaceDetailCopy: any = null;

  // 当前展示的接口ID
  @observable activeInterfaceId = '';

  // 当前展示的接口path
  @observable activeInterfacePath = '';

  // 初始化版本ID
  @observable activeEnvId = '';

  // 审核版本ID
  @observable auditId?: string;

  // 审核意见
  @observable auditMessage?: string;

  // 是否有接口数据
  @observable hasInterfaceData = true;

  // 展示全局的配置信息
  @observable configInfo = {
    toast: '', // 全局的消息提示
  };

  // 是否刷新页面中的数据
  @observable refreshFlag = false;

  // toubar是否显示isShowEditBtn按钮  通过滚动判断
  @observable isShowEditBtn = false;

  // 判断当前是否是 编辑状态
  @observable isEdit = false;

  // 初始化配置操作区
  @observable workspace = Type.ROUTER;

  // 初始化配置模式
  @observable workmode = ModeType.FORM;

  // 创建接口的弹出层
  @observable isShowAddInterfaceModal = false;

  // 更新IDL弹出层
  @observable isShowIDLModal = false;

  // 上线modal
  @observable isShowOnlineModal = false;

  // 展示属性设置的Modal
  @observable isShowAttrModal = false;

  // 添加API参数时  用来标识input focus
  @observable activeInputInfo = { id: '', type: '' };

  // 当前操作元素的属性
  @observable currentParamAttr: any = null;

  // 当前操作元素是否为子类型
  @observable isCurrentParamAttrSub = false;

  // 展示转换插件的Drawer
  @observable isShowPluginDrawer = false;

  // 展示转换插件list
  @observable pluginContentList: TransPluginItemContent[] = [];

  // 用于保存更改的转换插件的list
  @observable pluginUpdatedList: TransPluginUpdateItem[] = [];

  // 转换插件content模板
  @observable pluginModel = '';

  // 当前操作的转换插件的属性
  @observable currentPluginAttr: TransPluginItem | null = null;

  // 当前转换插件的更新函数
  @observable currentPlugiUpdater: ((newPluginItem: TransPluginItem) => void) | null = null;

  // 常用函数插件的Modal
  @observable isShowFuncPluginModal = false;

  // 常用函数库
  @observable pluginFuncLib: PluginFuncLib[] = [];

  // 当前操作的常用函数转换插件的属性
  @observable currentFuncPluginAttr: TransFuncPluginItem | null = null;

  // 当前转换插件的更新函数
  @observable currentFuncPlugiUpdater: ((newFuncPluginItem: TransFuncPluginItem) => void) | null = null;

  // 白名单
  @observable allowList = {};

  // 黑名单
  @observable denyList = {};

  // 服务列表
  @observable serviceList: ServiceListItem[] = [];

  @observable auditState = {
    canRelease: false, // 判断是否可以提交审核
    canCancel: false, // 判断是否可以撤销审核
  };

  @observable auditStatusCode = 1;

  // 页面滚动状态
  @observable pageStatus = {
    scrollLeft: 0,
    scrollTop: 0,
    changeFlag: 0,
  };

  // 页面模式切换
  @observable changeModelFlag = true;

  // 接口维度的标签
  @observable interfaceLabel: string[] = [];

  // 获取当前rpc方法名映射
  @computed get activeReqRpcFnNameList() {
    let key = '';
    try {
      key = camelCase(this.interfaceDetail.routeConfig.router.upstream.method);
      return this.rpcFnNameMap[key].requestParams;
    } catch (err) {
      return [];
    }
  }

  @computed get activeResRpcFnNameList() {
    let key = '';
    try {
      key = camelCase(this.interfaceDetail.routeConfig.router.upstream.method);
      return this.rpcFnNameMap[key].responseFields;
    } catch (err) {
      return [];
    }
  }

  @computed get canSubmitAudit() {
    const auditVersion = this.versionInfo.find((item) => item.name === ConfigVersionTypeEnum.Audit);
    // 101 是默认显示 审核版本的样式
    return !auditVersion || [AuditStatusEnum.Rejected, AuditStatusEnum.Expire, 101].includes(auditVersion?.auditStatus!);
  }

  @computed get auditStatus() {
    const auditVersion = this.versionInfo.find((item) => item.name === ConfigVersionTypeEnum.Audit);
    return auditVersion && auditVersion?.auditStatus;
  }

  // 构建url参数字符串
  @action
  buildUrlSearch() {
    let string = '';
    const teamId = getQueryParams('team_id');
    string = `?team_id=${teamId}`;
    if (this.serviceId) {
      string += `&service_id=${this.serviceId}`;
    }
    if (this.activeEnvType) {
      string += `&env_type=${this.activeEnvType}`;
    }
    if (this.activeInterfaceId) {
      string += `&interface_id=${this.activeInterfaceId}`;
    }
    if (this.activeEnvType === ConfigVersionTypeEnum.Audit && this.auditId) {
      string += `&audit_id=${this.auditId}`;
    }

    return string;
  }

  // 切换环境版本
  @action
  switchVersion(version: ConfigVersionTypeEnum) {
    this.activeEnvId = this.versionInfo.find((item: any) => item.name === version)?.environmentId || '';
    this.activeEnvType = version;
    this.interfaceList = [];
    this.hasInterfaceData = true;
    this.activeInterfaceId = '';
    this.interfaceKeyword = '';
    this.interfaceDetail = null;
    this.isEdit = false;
  }

  // 重置Store
  @action
  resetStore() {
    this.versionInfo = [];
    this.interfaceList = [];
    this.idlInfo = {
      commitId: '',
      commitTime: '',
      commitMessage: '',
    };
    this.rpcFnName = [];
    this.rpcFnNameMap = {};
    this.interfaceDetail = null;
    this.interfaceDetailCopy = null;
    this.activeInterfaceId = '';
    this.activeInterfacePath = '';
    this.activeEnvId = '';
    this.interfaceKeyword = '';
    this.activeInputInfo = { id: '', type: '' };
    this.hasInterfaceData = true;
    this.isEdit = false;
    this.workspace = Type.ROUTER;
    this.workmode = ModeType.FORM;
    this.isShowAddInterfaceModal = false;
    this.isShowIDLModal = false;
    this.isShowOnlineModal = false;
    this.currentServiceInfo = null;
    this.isShowAttrModal = false;
    this.currentParamAttr = null;
    this.pageStatus = {
      scrollLeft: 0,
      scrollTop: 0,
      changeFlag: 0,
    };
  }

  @action
  setActiveInputInfo(id = '', type = '') {
    this.activeInputInfo = { id, type };
  }

  // 修改API 属性配置参数
  /* eslint-disable no-param-reassign */
  @action
  apiConfigPropsChange(keyMap: { changeId: string; listType: string }, object: Record<string, any>) {
    const { changeId, listType } = keyMap;
    const origin = this.interfaceDetail[listType];
    const sortId = `${listType}Id`;
    const deleteProps = ['default', 'format', 'xEnumOpen', 'maximum', 'minimum', 'maxLength', 'minLength', 'example'];

    treeForeach(origin, (treeParent: TreeNode[], treeNode: TreeNode, treeKey: number) => {
      if (treeNode.id === changeId) {
        // 如果修改类型的话
        if (object.type || object.subType) { // 修改类型
          treeNode.subType = object.subType;
          treeNode.children = [{
            id: `${treeNode.id}-${this.interfaceDetail[sortId]++}`,
            type: object.subType,
            isExpand: true,
            children: [],
          }];
          // 类型修改后 需要删除 一些属性
          if (object.type) {
            treeNode.type = object.type;
            deleteProps.forEach((key) => {
              delete (treeNode as any)[key];
            });
          }
          if (isComplexType(object.subType)) {
            treeNode.children = [{
              id: `${treeNode.id}-${this.interfaceDetail[sortId]++}`,
              type: object.subType,
              subType: 'string',
              isExpand: true,
              children: [
                {
                  id: `${treeNode.id}-${this.interfaceDetail[sortId]++}-${this.interfaceDetail[sortId]++}`,
                  children: [],
                  isExpand: true,
                  type: 'string',
                },
              ],
            }];
          }
          if (object.subType) {
            treeNode.isExpand = true;
          }
          if (object.type === 'object' || isSimpleType(object.type)) { // 如果类型是object 需要重新构造
            treeNode.subType = '';
            treeNode.children = [];
          }
        } else { // 修改name等其他参数
          Object.keys(object).forEach((key: any) => {
            (treeNode as any)[key] = object[key];
          });
        }
        treeParent.splice(treeKey, 1, { ...treeNode });
      }

      this.interfaceDetail[listType] = toJS(origin);
    });
  }

  // 获取服务接口数据
  @action
  async getEnvList(isFirst = false) {
    const result = await getEnvironmentList({ serviceId: this.serviceId });
    // 无权限查看
    if (result.code === 40207) {
      Message.error(i18next.t('no_service_permission'));
      return;
    }
    if (result.code !== errCode.SUCCESS) {
      Message.error(i18next.t('get_interface_content_error'));
      return;
    }
    const list = get(result, 'data.environmentList', []);
    // 默认添加一个 audit
    if (list.findIndex((item: any) => item.name === 'audit') < 0) {
      list.splice(1, 0, {
        name: 'audit', auditId: '', auditMessage: '', auditStatus: 101,
      });
    }
    this.versionInfo = list;
    const {
      auditId, auditMessage, auditStates, auditStatus,
    } = list.find((item: EnvList) => item.name === ConfigVersionTypeEnum.Audit) || { auditId: '', auditMessage: '' };
    const { environmentId, configId } = list.find((item: EnvList) => item.name === this.activeEnvType) || { environmentId: '', configId: '' };
    this.activeEnvId = environmentId;
    if (auditStates) {
      this.auditState = auditStates;
      this.auditStatusCode = auditStatus;
    }
    this.auditId = auditId;
    this.auditMessage = auditMessage;
    // 首次调用时需要去请求 RPC 和 环境详情接口
    if (isFirst) {
      if (configId && this.currentServiceInfo?.serviceType !== 'http') {
        this.getRpcName(configId, environmentId);
      }
      this.getEnvDetail(environmentId);
    }
  }

  // 获取rpc服务名
  @action
  async getRpcName(configId: string, environmentId: string) {
    const result = await getRPCServiceName({
      configId,
      environmentId,
      serviceId: this.serviceId,
    });
    this.rpcFnName = get(result, 'data.methodList', []).map((item: string) => ({ label: item, value: item }));
    this.rpcFnNameMap = get(result, 'data.newMethodDict', {});
  }

  // 获取环境的基本信息
  @action
  async getEnvDetail(environmentId: string) {
    if (this.currentServiceInfo?.serviceType !== 'http') {
      const result = await getEnvironmentDetail({ serviceId: this.serviceId, environmentId });
      // 需要过滤后端误返回的null，不能用loadash的get方法
      this.idlInfo = result.data?.config?.idls?.lastCommit ?? {
        commitId: '',
        commitTime: '',
        commitMessage: '',
      };
      // this.idlInfo = get(result, 'data.config.idls.lastCommit', {
      //   commitId: '',
      //   commitTime: '',
      //   commitMessage: '',
      // });
    } else {
      this.idlInfo = {
        commitId: '',
        commitTime: '',
        commitMessage: '',
      };
    }
  }

  // 获取接口数据列表
  @action
  async getInterfaceList(reqBody = {}) {
    const { data, code } = await getInterfaceList({
      ...reqBody,
      page: 1,
      pageSize: 300,
      keyword: this.interfaceKeyword,
      serviceId: this.serviceId,
      environmentId: this.activeEnvId,
    });
    if (code !== errCode.SUCCESS) {
      Message.error(i18next.t('get_interface_list_error'));
      return;
    }
    this.interfaceList = get(data, 'interfaceList', []);
    this.hasInterfaceData = Boolean(this.interfaceList.length);
    // 如果找到与切换前路径相同的接口，则自动定位到该接口
    const index = this.interfaceList.findIndex((item) => item.path === this.activeInterfacePath);
    if (index !== -1) {
      this.activeInterfaceId = this.interfaceList[index].configId;
      this.activeInterfacePath = this.interfaceList[index].path;
    } else {
      // 如果activeInterfaceId不存在当前接口列表中，则替换为当前接口列表中第一个接口的id
      const ind = this.interfaceList.findIndex((item) => item.configId === this.activeInterfaceId);
      if (ind === -1) {
        this.activeInterfaceId = this.interfaceList[0]?.configId || '';
        this.activeInterfacePath = this.interfaceList[0]?.path || '';
      } else {
        // activeInterfaceId存在时匹配path
        this.activeInterfacePath = this.interfaceList[ind].path;
      }
    }
  }

  // 代码模式下获取接口详情
  @action
  async getInterfaceDetail() {
    const result = await getInterfaceDetail({
      serviceId: this.serviceId,
      environmentId: this.activeEnvId,
      configId: this.activeInterfaceId,
    });
    // 由于兼容 -1 等一系列特需的key值， 强行兼容  调用 get1 post1方案 获取数据后转驼峰
    const newResult = snakeToCamle(result, disabledSnakeFields);
    const { code, data } = newResult;
    if (code !== errCode.SUCCESS) {
      Message.error(i18next.t('get_interface_content_error'));
    }
    this.activeInterfacePath = data.path;

    this.formateCodeMode(data);
  }

  // 获取接口详情新
  @action
  async getNewInterfaceDetail() {
    const result = await getNewInterfaceDetail({
      serviceId: this.serviceId,
      environmentId: this.activeEnvId,
      configId: this.activeInterfaceId,
    });
    const newResult = snakeToCamle(result, disabledSnakeFields);
    const { code, data } = newResult;
    if (code !== errCode.SUCCESS) {
      Message.error(i18next.t('get_interface_content_error'));
      return;
    }
    this.activeInterfacePath = data.path;
    this.formateFormMode(data);
  }

  @action
  setActiveEnvType(type: ConfigVersionTypeEnum) {
    this.activeEnvType = type;
  }

  // 获取服务列表
  @action
  async getServices(teamId: string) {
    this.serviceList = [];
    try {
      const { data, code } = await getServiceList({
        page: 1,
        pageSize: 100,
        teamId,
      });
      if (code === errCode.SUCCESS) {
        this.serviceList = data.serviceList || [];
        return;
      }
      Message.error(i18next.t('get_service_list_error'));
    } catch (err) {
      Message.error(i18next.t('get_service_list_error'));
    }
  }

  // 获取服务的接口
  @action
  async getServiceInfo() {
    const teamId = getQueryParams('team_id');
    const result = await getServiceInfo({
      serviceId: this.serviceId,
      teamId,
    });
    if (result.code !== errCode.SUCCESS) {
      Message.error(i18next.t('get_service_info_error'));
      return;
    }
    this.currentServiceInfo = get(result, 'data.serviceList[0]', null);
  }

  // 删除接口
  @action
  async deleteInterface(index: number, configId: string) {
    const result = await deleteInterface({
      serviceId: this.serviceId,
      environmentId: this.activeEnvId,
      configId,
    });

    if (result.code !== errCode.SUCCESS) {
      Message.error(result.msg);
      return Promise.resolve('error');
    }
    if (configId === this.activeInterfaceId) {
      const list = toJS(this.interfaceList);
      list.splice(index, 1);
      this.interfaceList = list;
      const { length } = this.interfaceList;
      const idx = Math.min(length - 1, index);
      this.activeInterfaceId = length ? this.interfaceList[idx].configId : '';
      if (length === 0) {
        this.hasInterfaceData = false;
      }
    } else {
      this.interfaceList.splice(index, 1);
    }
    Message.success(i18next.t('delete_interface_success'));
    return Promise.resolve('success');
  }

  // 更新接口信息 -- 新
  @action
  // eslint-disable-next-line class-methods-use-this
  async updateNewInterface(data: any) {
    const result = await updateNewInterface(data);
    if (result.code !== errCode.SUCCESS) {
      Message.error(i18next.t('error'));
      return Promise.resolve('error');
    }
    // 重新获取插件content列表，并清空插件更新列表
    this.getPlugins();
    this.pluginUpdatedList = [];

    this.interfaceDetail.isSubmit = false;
    this.interfaceDetailCopy = toJS(this.interfaceDetail);
    this.interfaceDetail = toJS(this.interfaceDetail);
    this.isEdit = false;
    Message.success('update_success');
    return Promise.resolve('success');
  }

  // 更新接口信息 -- 旧
  @action
  async updateInterface() {
    const result = await updateInterface({
      ...this.codeModeDetail,
      customPluginList: toJS(this.pluginUpdatedList),
    });
    if (result.code !== errCode.SUCCESS) {
      Message.error(i18next.t('error'));
      return Promise.resolve('error');
    }
    // 重新获取插件content列表，并清空插件更新列表
    this.getPlugins();
    this.pluginUpdatedList = [];

    this.isEdit = false;
    this.codeModeDetailCopy = toJS(this.codeModeDetail);
    // 重新查询数据
    // this.interfaceDetail = null;
    // this.getNewInterfaceDetail();
    // this.getInterfaceDetail();
    Message.success('update_success');
    return Promise.resolve('success');
  }

  // 表单模式转化代码模式
  @action
  async transferCodeModel(body: any) {
    const result = await transferCodeModel({
      ...body,
    });
    // 由于兼容 -1 等一系列特需的key值， 强行兼容  调用 get1 post1方案 获取数据后转驼峰
    const newResult = snakeToCamle(result, disabledSnakeFields);
    const { code, data } = newResult;
    if (code !== errCode.SUCCESS) {
      return Promise.resolve('error');
    }

    this.formateCodeMode(data);
  }

  // 代码模式转化为表单模式
  @action
  async transferFormModel() {
    this.changeModelFlag = !this.changeModelFlag;
    const result = await transferFormModel(toJS(this.codeModeDetail));
    const newResult = snakeToCamle(result, disabledSnakeFields);
    const { code, data } = newResult;
    if (code !== errCode.SUCCESS) {
      return Promise.resolve('error');
    }

    this.formateFormMode(data);
  }

  // 结构化 代码模式代码
  @action
  formateCodeMode(data: any) {
    this.codeModeDetail = {
      path: data.path,
      method: data.method,
      configId: data.configId,
      serviceId: data.serviceId,
      environmentId: data.environmentId,
      routeConfig: data.routeConfig,
      apiConfig: data.apiConfig,
      activePublish: data.activePublish,
      customPluginList: data.customPluginList,
    };
    this.codeModeDetailCopy = toJS(this.codeModeDetail);
  }

  // 结构化 表单模式返回参数
  @action
  formateFormMode(data: any) {
    // 临时支持方案
    if (!data.routeConfig.plugins.creatorIdMapper) {
      // eslint-disable-next-line no-param-reassign
      data.routeConfig.plugins.creatorIdMapper = { enabled: false };
    }
    const { source: reqQuery, ids: reqQueryId } = buildApiConfig(get(data, 'newApiConfig.httpQuery', []));
    const { source: reqBody, ids: reqBodyId } = buildApiConfig(get(data, 'newApiConfig.httpBody', []));
    const { source: reqHeader, ids: reqHeaderId } = buildApiConfig(get(data, 'newApiConfig.httpHeader', []));
    const { source: resBody, ids: resBodyId } = buildApiConfig(get(data, 'newApiConfig.httpResp', []));
    const { source: rpc, ids: rpcId } = this.currentServiceInfo?.serviceType === 'http'
      ? buildApiConfig(get(data, 'newApiConfig.httpSource', [])) : buildApiRpcConfig(get(data, 'newApiConfig.httpSource', []));
    const { source: lrQuery, ids: lrQueryId } = buildApiConfig(get(data, 'newApiConfig.httpQuerySource', []));
    const { source: baseSource, ids: baseSourceId } = buildApiConfig(get(data, 'newApiConfig.baseSource', []));
    const origin = {
      otherConfig: { ...data.newApiConfig },
      reqQuery,
      reqQueryId,
      reqHeader,
      reqHeaderId,
      reqBody,
      reqBodyId,
      resBody,
      resBodyId,
      rpc,
      rpcId,
      lrQuery,
      lrQueryId,
      baseSource,
      baseSourceId,
      reqPlugin: get(data, 'newApiConfig.httpPlugins.beforeRequest', []),
      resPlugin: get(data, 'newApiConfig.httpPlugins.afterResponse', []),
      method: data.method,
      path: data.path,
      configId: data.configId,
      serviceId: data.serviceId,
      environmentId: data.environmentId,
      title: get(data, 'newApiConfig.title', ''),
      routeConfig: get(data, 'routeConfig', {}),
      description: get(data, 'newApiConfig.description', ''),
      reqType: get(data, 'newApiConfig.requestContentType', 'application/json'),
      resType: get(data, 'newApiConfig.responseContentType', 'application/json'),
      rpcConfigMap: {}, // 子集平铺展开的项
      appFreqList: transformObjectToArray(data.routeConfig.plugins.appFreqLimit.appidLimit), // 频控的参数
      defaultErrorCode: getDefaultCode(data.routeConfig.router.upstream.error.errMap),
      errorCodeList: codeObjectToArray(data.routeConfig.router.upstream.error.errMap), // 错误码
      defaultSuccessCode: getDefaultCode(data.routeConfig.router.upstream.error.successMap),
      successCodeList: codeObjectToArray(data.routeConfig.router.upstream.error.successMap), // 成功码
      isSubmit: false, // 判断是否点击提交按钮 用来做校验
      activePublish: Boolean(data.activePublish), // 是否需要发布后生效
    };
    this.allowList = get(data, 'newApiConfig.xOpenAllowlist');
    this.denyList = get(data, 'newApiConfig.xOpenDenylist');
    this.interfaceLabel = get(data, 'newApiConfig.xOpenSceneLabels', []);
    this.interfaceDetail = origin;
    this.interfaceDetailCopy = cloneDeep(origin);
  }

  // 创建、更新标签
  @action
  async createLabel(list: string[]) {
    const result = await updateInterfaceLabel({
      teamId: getQueryParams('team_id') as string,
      serviceId: this.serviceId,
      configId: this.activeInterfaceId,
      environmentId: this.activeEnvId,
      sceneLabelList: list,
    });
    if (result.code !== errCode.SUCCESS) {
      Message.error(i18next.t('create_label_error'));
      return Promise.resolve('error');
    }
    return Promise.resolve('success');
  }

  /**
   * 获取自定义函数插件
   */
  @action
  async getPlugins() {
    try {
      const result = await getPlugins({
        teamId: getQueryParams('team_id')!,
        serviceId: this.serviceId,
        environmentId: this.activeEnvId,
        page: 1,
        pageSize: 20,
        // (不传pluginName为获取所有)
      });
      if (result.code !== errCode.SUCCESS) {
        Message.error(i18next.t('plugin_func_get_err'));
        return Promise.resolve('error');
      }
      this.pluginContentList = result.data.pluginList;
      return Promise.resolve('success');
    } catch (error) {
      return Promise.resolve('error');
    }
  }

  /**
   * 获取自定义函数插件content模板
   */
  @action
  async getPluginModel() {
    try {
      const result = await getPluginModel({
        label: 'custom_plugin',
      });
      if (result.code !== errCode.SUCCESS) {
        Message.error(i18next.t('plugin_func_get_model_err'));
        return Promise.resolve('error');
      }
      this.pluginModel = result.data.content;
      return Promise.resolve('success');
    } catch (error) {
      return Promise.resolve('error');
    }
  }

  /**
   * 接受一个 TransPluginUpdateItem，自动合入 pluginUpdatedList
   */
  @action
  async updatePluginUpdatedList(pluginItem: TransPluginUpdateItem, isDelete = false) {
    if (!isDelete) {
      if (this.pluginUpdatedList.length >= 3) {
        // 单次编辑最多只可更新3个自定义插件
        return Modal.alert({
          size: 'sm',
          title: i18next.t('tip'),
          children: i18next.t('plugin_func_update_warning', { count: 3 }),
        });
      }
      const cloneList = toJS(this.pluginUpdatedList);
      const ind = cloneList.findIndex((item) => item.name === pluginItem.name);
      if (ind === -1) {
        this.pluginUpdatedList = [...cloneList, pluginItem];
      } else {
        cloneList.splice(ind, 1);
        this.pluginUpdatedList = [...cloneList, pluginItem];
      }
    } else {
      // isDelete === true 时表示删除操作
      const cloneList = toJS(this.pluginUpdatedList);
      const ind = cloneList.findIndex((item) => item.name === pluginItem.name);
      if (ind !== -1) {
        this.pluginUpdatedList = cloneList.splice(ind, 1);
      }
    }
  }

  /**
   * 获取常用函数库
   */
  @action
  async getFuncPluginLib() {
    // 避免重复获取
    if (this.pluginFuncLib.length) {
      return;
    }
    try {
      const result = await getFuncPluginLib();
      if (result.code !== errCode.SUCCESS) {
        Message.error(i18next.t('plugin_func_get_lib_err'));
        return Promise.resolve('error');
      }
      this.pluginFuncLib = result.data.paramsFunctionList;
      return Promise.resolve('success');
    } catch (error) {
      return Promise.resolve('error');
    }
  }
}
export default { name: 'configStore', Store: ConfigStore };
